<template>
  <SwiperSlide
    v-for="{ course, id } in courseTiles"
    :key="id"
    class="course-slide"
  >
    <NuxtLink :to="getLinkTo(course)" class="course-tile">
      <div class="content">
        <div class="header">
          <div class="col">
            <TextClampedTooltip>
              <p
                ref="titleRefs"
                class="title t-h5"
                v-html="nbsp(course.title)"
              />

              <template #content>
                <p class="t-p-s-130-bold" v-html="nbsp(course.title)" />
                <p class="t-p-s-130 t-color-2" v-html="nbsp(course.name)" />
              </template>
            </TextClampedTooltip>
            <TextClampedTooltip>
              <p class="description t-p-s-130" v-html="nbsp(course.name)" />

              <template #content>
                <p class="t-p-s-130-bold" v-html="nbsp(course.title)" />
                <p class="t-p-s-130 t-color-2" v-html="nbsp(course.name)" />
              </template>
            </TextClampedTooltip>
          </div>
          <BaseButton tag="span" color="function-black" :icon="ArrowRightM" />
        </div>

        <p
          class="level t-p-s-130"
          v-html="nbsp(t(`course-level.${course.course_level}`))"
        />
      </div>
      <BaseImage
        :img-sizes="[{ w: 384, h: 410 }]"
        class="background-image fill-space"
        :image="course.image"
      />
    </NuxtLink>
  </SwiperSlide>
</template>

<script setup lang="ts">
import ArrowRightM from 'assets/icons/arrow-right-m.svg?raw'

import type {
  PublicCourseTile,
  SimplePublicCourseAndEventPage
} from '@/service/__generated-api'

const props = defineProps<{ courseTiles: PublicCourseTile[] }>()

const { t } = useI18n()
const localePath = useLocalePath()
const getLinkTo = (course: SimplePublicCourseAndEventPage) =>
  localePath({
    name: 'course-slug',
    params: { slug: course.slug }
  })

const titleRefs = useElementsSameHeight({
  watchSource: () => props.courseTiles,
  resizeFrom: 'mobile'
})
</script>

<style scoped lang="scss">
@keyframes fade {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.course-slide {
  width: auto;
  animation: fade;
  animation-duration: 1s;
}

.course-tile {
  position: relative;
  display: flex;
  align-items: flex-end;
  width: calc(300 / 360 * 100svw);
  max-width: rem(384px);
  height: rem(410px);
  padding: rem(8px);
  border-radius: $border-radius;

  @include card-hover-shadow {
    border-radius: $border-radius;
  }
}

.background-image {
  z-index: -1;
  overflow: hidden;
  border-radius: $border-radius;

  :deep(img) {
    @include card-hover-image-scale('.course-tile:hover &');
  }
}

.content {
  @include flex-col;

  z-index: 1;
  justify-content: space-between;
  width: 100%;
  min-height: rem(151px);
  padding: rem(12px) rem(18px);
  background-color: $layout-background-02;
  border-radius: $border-radius-small;
}

.header {
  display: flex;
  gap: rem(30px);
  align-items: flex-start;
  justify-content: space-between;
}

.col {
  @include flex-col;
  gap: rem(4px);
}

.title,
.description {
  @include line-clamp;
}

.description {
  color: $layout-text-03;
}

.level {
  padding-top: rem(6px);
  border-top: 1px solid $layout-divider;
}
</style>
